import { AppBar, Toolbar, Box, Button, useTheme, Avatar, Typography, useMediaQuery, ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, Menu, MenuItem, Chip, TextField, InputAdornment, } from "@mui/material";
import { FIconButton } from "@ui/components/buttons/IButton";
import { styled } from "@mui/material/styles";
const HeaderIconButton = styled(FIconButton)(({ theme }) => ({
    border: "1px solid #FFFFFF52",
    borderRadius: "500px",
}));
import { Icon } from "@iconify/react";
import { ArrowDown2, SearchNormal, } from "iconsax-react";
import React, { useState, useEffect, useRef } from "react";
import { FMenu } from "@ui/components/menu/FMenu";
import LogoutIcon from "@ui/components/icons/LogoutIcon";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../auth/useAuth";
import FDrawer from "@ui/components/drawer/FDrawer";
import { useCompany } from "../../context/CompanyContext";
import { usePermissions } from "../../context/PermissionContext";
const Header = () => {
    var _a, _b, _c;
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const isTablet = useMediaQuery((theme) => theme.breakpoints.down("md"));
    const { logout, account } = useAuth();
    const { canRead } = usePermissions();
    // Use company context
    const { selectedCompanies, setSelectedCompanies, companies, isLoadingCompanies, hasPendingChanges, setHasPendingChanges, } = useCompany();
    const [selectedLanguage, setSelectedLanguage] = useState("NL");
    const debounceTimerRef = useRef(null);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [languageMenuAnchor, setLanguageMenuAnchor] = useState(null);
    const [avatarMenuAnchor, setAvatarMenuAnchor] = useState(null);
    const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
    const [mobileCompanyMenuAnchor, setMobileCompanyMenuAnchor] = useState(null);
    const [mobileLanguageMenuAnchor, setMobileLanguageMenuAnchor] = useState(null);
    const [companySearchQuery, setCompanySearchQuery] = useState("");
    const [mobileCompanySearchQuery, setMobileCompanySearchQuery] = useState("");
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const handleMenuOpen = (event) => {
        setMenuAnchor(event.currentTarget);
    };
    const handleLanguageMenuOpen = (event) => {
        setLanguageMenuAnchor(event.currentTarget);
    };
    const handleAvatarMenuOpen = (event) => {
        if (isMobile) {
            setMobileDrawerOpen(true);
        }
        else {
            setAvatarMenuAnchor(event.currentTarget);
        }
    };
    const handleMobileCompanyMenuOpen = (event) => {
        setMobileCompanyMenuAnchor(event.currentTarget);
    };
    const handleMobileLanguageMenuOpen = (event) => {
        setMobileLanguageMenuAnchor(event.currentTarget);
    };
    const handleMobileCompanyMenuClose = () => {
        setMobileCompanyMenuAnchor(null);
        setMobileCompanySearchQuery(""); // Clear search when menu closes
    };
    const handleMobileLanguageMenuClose = () => {
        setMobileLanguageMenuAnchor(null);
    };
    const handleMenuClose = () => {
        setMenuAnchor(null);
        setCompanySearchQuery(""); // Clear search when menu closes
    };
    const handleLanguageMenuClose = () => {
        setLanguageMenuAnchor(null);
    };
    const handleAvatarMenuClose = () => {
        setAvatarMenuAnchor(null);
    };
    const handleMobileDrawerOpen = () => {
        setMobileDrawerOpen(true);
    };
    const handleMobileDrawerClose = () => {
        setMobileDrawerOpen(false);
    };
    const handleSelectLanguage = (lang) => {
        setSelectedLanguage(lang);
        handleLanguageMenuClose();
        handleMobileLanguageMenuClose();
        // Add language switching logic here if needed
    };
    const languageMenuItems = [
        {
            label: "NL",
            value: "NL",
            icon: (React.createElement(Box, { component: "img", src: "/assets/images/flags/nl-flag.svg", alt: "NL", sx: { width: 20, height: 20 } })),
        },
    ];
    const avatarMenuItems = [
        {
            label: "Projecten",
            color: "default",
            icon: React.createElement(Icon, { icon: "hugeicons:folder-03", width: 20, height: 20 }),
            onClick: () => {
                handleAvatarMenuClose();
                navigate("/project");
            },
            sx: location.pathname.startsWith("/project")
                ? { fontWeight: "bold" }
                : {},
        },
        {
            label: "Instellingen",
            color: "default",
            icon: React.createElement(Icon, { icon: "mdi:cog", width: 20, height: 20 }),
            onClick: () => {
                handleAvatarMenuClose();
                navigate("/settings");
            },
            sx: location.pathname.startsWith("/settings")
                ? { fontWeight: "bold" }
                : {},
            permission: true,
        },
        {
            label: "Logout",
            color: "error",
            icon: React.createElement(LogoutIcon, { size: 20 }),
            onClick: () => {
                handleAvatarMenuClose();
                logout();
            },
            permission: true,
        },
    ].filter((item) => item.permission !== false);
    function handleSelectCompany(name) {
        setSelectedCompanies((prev) => {
            // Single selection only - always replace with new selection
            const newSelection = [name];
            // Check if selection actually changed
            if (prev.length !== 1 || prev[0] !== name) {
                // Clear any existing timer
                if (debounceTimerRef.current) {
                    clearTimeout(debounceTimerRef.current);
                }
                // Show pending changes indicator
                setHasPendingChanges(true);
                // Set new timer for 3 seconds
                debounceTimerRef.current = setTimeout(() => {
                    // Clear pending changes flag before navigating so the page can fetch data
                    setHasPendingChanges(false);
                    // If already on project page, reload to trigger data fetch
                    // Otherwise navigate to project page
                    if (location.pathname === "/project") {
                        window.location.reload();
                    }
                    else {
                        navigate("/project");
                    }
                }, 3000);
            }
            return newSelection;
        });
        // Close menu after selection
        handleMenuClose();
        handleMobileCompanyMenuClose();
    }
    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);
    // Get display text for selected companies
    const getCompanyDisplayText = () => {
        if (selectedCompanies.length === 0)
            return "Selecteer bedrijf";
        return selectedCompanies[0];
    };
    // Get user initials from account
    const getUserInitials = () => {
        if (!(account === null || account === void 0 ? void 0 : account.username))
            return "U";
        // Extract name from email (part before @) or use username directly
        const namePart = account.username.split("@")[0];
        // Try to extract initials from name (handle cases like "john.doe" or "john doe")
        const nameParts = namePart.replace(/[._]/g, " ").split(" ").filter(Boolean);
        if (nameParts.length >= 2) {
            return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
        }
        // If single name, use first two characters
        return namePart.substring(0, 2).toUpperCase();
    };
    // Filter companies based on search query and sort selected companies to top
    const getFilteredCompanies = (searchQuery) => {
        if (!companies)
            return [];
        const query = searchQuery.toLowerCase().trim();
        // Filter companies and create a copy to avoid mutating read-only array
        const filtered = !query
            ? [...companies]
            : companies.filter((company) => company.entiteitNaam.toLowerCase().includes(query));
        // Sort: selected companies first, then unselected
        return filtered.sort((a, b) => {
            const aSelected = selectedCompanies.includes(a.entiteitNaam);
            const bSelected = selectedCompanies.includes(b.entiteitNaam);
            if (aSelected && !bSelected)
                return -1;
            if (!aSelected && bSelected)
                return 1;
            return 0;
        });
    };
    return (React.createElement(AppBar, { position: "fixed", color: "default", elevation: 0, sx: {
            background: theme.palette.primary.darker,
            pl: 3.5,
            pr: isTablet ? 4 : 6,
            py: isTablet ? 1 : 2.3,
            position: "relative",
            overflow: "visible",
            zIndex: 0,
        } },
        React.createElement(Toolbar, { disableGutters: true, sx: {
                justifyContent: "space-between",
                position: "relative",
                zIndex: 1,
            } },
            React.createElement(Box, { sx: { display: "flex", alignItems: "center" } },
                React.createElement(Box, { component: "img", src: "/assets/images/logo.svg", alt: "Logo", height: 64, onClick: () => navigate("/project"), sx: {
                        cursor: "pointer",
                        "&:hover": {
                            opacity: 0.8,
                        },
                    } })),
            React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1 } },
                !isMobile && (React.createElement(React.Fragment, null,
                    React.createElement(Button, { variant: "outlined", style: {
                            border: "1px solid #FFFFFF52",
                            borderRadius: "40px",
                            color: theme.palette.primary.contrastText,
                            backgroundColor: hasPendingChanges
                                ? "rgba(255, 255, 255, 0.1)"
                                : "transparent",
                        }, startIcon: React.createElement(Icon, { icon: "hugeicons:building-03", width: 20, height: 20 }), endIcon: React.createElement(ArrowDown2, { size: 18 }), color: "inherit", onClick: handleMenuOpen },
                        getCompanyDisplayText(),
                        hasPendingChanges && (React.createElement(Chip, { label: "Toepassen...", size: "small", sx: {
                                ml: 1,
                                height: 20,
                                fontSize: "0.7rem",
                                backgroundColor: "rgba(255, 255, 255, 0.2)",
                                color: theme.palette.primary.contrastText,
                            } }))),
                    React.createElement(Menu, { anchorEl: menuAnchor, open: Boolean(menuAnchor), onClose: handleMenuClose, PaperProps: {
                            sx: {
                                maxHeight: 400,
                                minWidth: 250,
                            },
                        } },
                        React.createElement(Box, { sx: { px: 1, py: 0.5 } },
                            React.createElement(TextField, { fullWidth: true, size: "small", placeholder: "Zoek bedrijven...", value: companySearchQuery, onChange: (e) => setCompanySearchQuery(e.target.value), onClick: (e) => e.stopPropagation(), onKeyDown: (e) => e.stopPropagation(), InputProps: {
                                    startAdornment: (React.createElement(InputAdornment, { position: "start" },
                                        React.createElement(SearchNormal, { size: 18 }))),
                                }, sx: {
                                    "& .MuiOutlinedInput-root": {
                                        backgroundColor: "background.paper",
                                    },
                                } })),
                        React.createElement(Divider, null),
                        getFilteredCompanies(companySearchQuery).length > 0 ? (getFilteredCompanies(companySearchQuery).map((company) => (React.createElement(MenuItem, { key: company.entiteitId, onClick: () => handleSelectCompany(company.entiteitNaam), dense: true, selected: selectedCompanies.includes(company.entiteitNaam) },
                            React.createElement(ListItemText, { primary: company.entiteitNaam }))))) : (React.createElement(MenuItem, { disabled: true },
                            React.createElement(ListItemText, { primary: "Geen bedrijven gevonden" })))),
                    React.createElement(HeaderIconButton, { size: "medium", title: "Taal wijzigen", onClick: handleLanguageMenuOpen }, ((_a = languageMenuItems.find((item) => item.value === selectedLanguage)) === null || _a === void 0 ? void 0 : _a.icon) || languageMenuItems[0].icon),
                    React.createElement(FMenu, { anchorEl: languageMenuAnchor, open: Boolean(languageMenuAnchor), handleClose: handleLanguageMenuClose, menuItems: languageMenuItems.map((item) => ({
                            label: item.label,
                            icon: item.icon,
                            onClick: () => handleSelectLanguage(item.value),
                        })) }))),
                React.createElement(HeaderIconButton, { size: "medium", title: "Gebruikersmenu" },
                    React.createElement(Avatar, { variant: "circular", sx: {
                            backgroundColor: "transparent",
                            border: "1px solid #FFFFFF52",
                            color: theme.palette.primary.contrastText,
                            cursor: "pointer",
                        }, onClick: handleAvatarMenuOpen },
                        React.createElement(Typography, { variant: "subtitle2", color: "primary.contrastText" }, getUserInitials()))),
                !isMobile && (React.createElement(FMenu, { anchorEl: avatarMenuAnchor, open: Boolean(avatarMenuAnchor), handleClose: handleAvatarMenuClose, menuItems: avatarMenuItems })),
                isMobile && (React.createElement(FDrawer, { open: mobileDrawerOpen, onClose: handleMobileDrawerClose, header: React.createElement(Typography, { variant: "h6", fontWeight: 500 }, "Instellingen") },
                    React.createElement(Box, { sx: { display: "flex", flexDirection: "column", gap: 2 } },
                        React.createElement(Box, null,
                            React.createElement(Typography, { variant: "subtitle2", sx: {
                                    px: 2,
                                    py: 1,
                                    color: "text.secondary",
                                    fontWeight: 600,
                                    fontSize: "0.75rem",
                                    textTransform: "uppercase",
                                } }, "Bedrijf"),
                            React.createElement(ListItem, { disablePadding: true },
                                React.createElement(ListItemButton, { onClick: handleMobileCompanyMenuOpen },
                                    React.createElement(ListItemIcon, null,
                                        React.createElement(Icon, { icon: "hugeicons:building-03", width: 20, height: 20 })),
                                    React.createElement(ListItemText, { primary: getCompanyDisplayText() }),
                                    React.createElement(ArrowDown2, { size: 18 }))),
                            React.createElement(Menu, { anchorEl: mobileCompanyMenuAnchor, open: Boolean(mobileCompanyMenuAnchor), onClose: handleMobileCompanyMenuClose, PaperProps: {
                                    sx: {
                                        maxHeight: 400,
                                        minWidth: 250,
                                    },
                                } },
                                React.createElement(Box, { sx: { px: 1, py: 0.5 } },
                                    React.createElement(TextField, { fullWidth: true, size: "small", placeholder: "Zoek bedrijven...", value: mobileCompanySearchQuery, onChange: (e) => setMobileCompanySearchQuery(e.target.value), onClick: (e) => e.stopPropagation(), onKeyDown: (e) => e.stopPropagation(), InputProps: {
                                            startAdornment: (React.createElement(InputAdornment, { position: "start" },
                                                React.createElement(SearchNormal, { size: 18 }))),
                                        }, sx: {
                                            "& .MuiOutlinedInput-root": {
                                                backgroundColor: "background.paper",
                                            },
                                        } })),
                                React.createElement(Divider, null),
                                getFilteredCompanies(mobileCompanySearchQuery).length >
                                    0 ? (getFilteredCompanies(mobileCompanySearchQuery).map((company) => (React.createElement(MenuItem, { key: company.entiteitId, onClick: () => handleSelectCompany(company.entiteitNaam), dense: true, selected: selectedCompanies.includes(company.entiteitNaam) },
                                    React.createElement(ListItemText, { primary: company.entiteitNaam }))))) : (React.createElement(MenuItem, { disabled: true },
                                    React.createElement(ListItemText, { primary: "Geen bedrijven gevonden" }))))),
                        React.createElement(Divider, null),
                        React.createElement(Box, null,
                            React.createElement(Typography, { variant: "subtitle2", sx: {
                                    px: 2,
                                    py: 1,
                                    color: "text.secondary",
                                    fontWeight: 600,
                                    fontSize: "0.75rem",
                                    textTransform: "uppercase",
                                } }, "Taal"),
                            React.createElement(ListItem, { disablePadding: true },
                                React.createElement(ListItemButton, { onClick: handleMobileLanguageMenuOpen },
                                    React.createElement(ListItemIcon, null, ((_b = languageMenuItems.find((item) => item.value === selectedLanguage)) === null || _b === void 0 ? void 0 : _b.icon) || languageMenuItems[0].icon),
                                    React.createElement(ListItemText, { primary: ((_c = languageMenuItems.find((item) => item.value === selectedLanguage)) === null || _c === void 0 ? void 0 : _c.label) || languageMenuItems[0].label }),
                                    React.createElement(ArrowDown2, { size: 18 }))),
                            React.createElement(FMenu, { anchorEl: mobileLanguageMenuAnchor, open: Boolean(mobileLanguageMenuAnchor), handleClose: handleMobileLanguageMenuClose, menuItems: languageMenuItems.map((item) => ({
                                    label: item.label,
                                    icon: item.icon,
                                    onClick: () => handleSelectLanguage(item.value),
                                })) })),
                        React.createElement(Divider, null),
                        React.createElement(Box, null,
                            React.createElement(Typography, { variant: "subtitle2", sx: {
                                    px: 2,
                                    py: 1,
                                    color: "text.secondary",
                                    fontWeight: 600,
                                    fontSize: "0.75rem",
                                    textTransform: "uppercase",
                                } }, "Navigatie"),
                            React.createElement(ListItem, { disablePadding: true },
                                React.createElement(ListItemButton, { onClick: () => {
                                        handleMobileDrawerClose();
                                        navigate("/project");
                                    } },
                                    React.createElement(ListItemIcon, null,
                                        React.createElement(Icon, { icon: "hugeicons:folder-03", width: 20, height: 20 })),
                                    React.createElement(ListItemText, { primary: "Projecten" }))),
                            canRead("Users") && (React.createElement(ListItem, { disablePadding: true },
                                React.createElement(ListItemButton, { onClick: () => {
                                        handleMobileDrawerClose();
                                        navigate("/users");
                                    } },
                                    React.createElement(ListItemIcon, null,
                                        React.createElement(Icon, { icon: "mdi:account-group", width: 20, height: 20 })),
                                    React.createElement(ListItemText, { primary: "Gebruikers" })))),
                            canRead("RolesPermissions") && (React.createElement(ListItem, { disablePadding: true },
                                React.createElement(ListItemButton, { onClick: () => {
                                        handleMobileDrawerClose();
                                        navigate("/roles-permissions");
                                    } },
                                    React.createElement(ListItemIcon, null,
                                        React.createElement(Icon, { icon: "hugeicons:folder-03", width: 20, height: 20 })),
                                    React.createElement(ListItemText, { primary: "Role & Permissions" }))))),
                        React.createElement(Divider, null),
                        React.createElement(ListItem, { disablePadding: true },
                            React.createElement(ListItemButton, { onClick: () => {
                                    handleMobileDrawerClose();
                                    logout();
                                }, sx: {
                                    color: "error.main",
                                    "&:hover": {
                                        backgroundColor: (theme) => theme.palette.error.lighter,
                                    },
                                    "& .MuiListItemIcon-root": {
                                        color: "error.main",
                                    },
                                } },
                                React.createElement(ListItemIcon, null,
                                    React.createElement(LogoutIcon, { size: 20 })),
                                React.createElement(ListItemText, { primary: "Logout" }))))))))));
};
export default Header;
